import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Package, ChevronDown, X, Search, MapPin, User } from 'lucide-react';
import KPICard from 'components/common/KPICard';
import produtosUsadosOSService from 'features/estoque/services/produtosUsadosOSService';
import { DateRangeFilterBlur } from 'components/filters';
import ClearCacheButton from 'features/estoque/components/ClearCacheButton';
import PeriodoDisplay from '../PeriodoDisplay';

// Importar componentes específicos
import Top10Dashboard from './Components/Top10Dashboard';
import ProdutosUsadosPorOSSTable from './Components/ProdutosUsadosPorOSSTable';
import DetalhamentoOSSTable from './Components/DetalhamentoOSSTable';
import AnaliseProdutosMovimentadosTable from './Components/AnaliseProdutosMovimentadosTable';
import EvolucaoCustoMensalChart from './Components/EvolucaoCustoMensalChart';

const ProdutosUsadosOS = () => {
  const [dadosCompilados, setDadosCompilados] = useState({
    produtos_usados_por_oss: [],
    ranking_top10_produtos_por_cidade: [],
    detalhamento_oss_produtos_utilizados: [],
    top10_qtd_produtos_por_cidade: [],
    analise_produtos_movimentados_oss: [],
    evolucao_custo_12m: [],
    assuntos_disponiveis: []
  });
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [ultimaAtualizacao, setUltimaAtualizacao] = useState(null);
  const [assuntosSelecionados, setAssuntosSelecionados] = useState([]);
  const [assuntosSelecionadosDraft, setAssuntosSelecionadosDraft] = useState([]);
  const [dropdownAssuntosAberto, setDropdownAssuntosAberto] = useState(false);
  const [buscaAssunto, setBuscaAssunto] = useState('');
  const [cidadesSelecionadas, setCidadesSelecionadas] = useState([]);
  const [cidadesSelecionadasDraft, setCidadesSelecionadasDraft] = useState([]);
  const [dropdownCidadesAberto, setDropdownCidadesAberto] = useState(false);
  const [buscaCidade, setBuscaCidade] = useState('');
  const [tecnicosSelecionados, setTecnicosSelecionados] = useState([]);
  const [tecnicosSelecionadosDraft, setTecnicosSelecionadosDraft] = useState([]);
  const [dropdownTecnicosAberto, setDropdownTecnicosAberto] = useState(false);
  const [buscaTecnico, setBuscaTecnico] = useState('');
  const dropdownAssuntosRef = useRef(null);
  const dropdownCidadesRef = useRef(null);
  const dropdownTecnicosRef = useRef(null);
  const today = new Date();
  const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const pad = (n) => String(n).padStart(2, '0');
  const toISO = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const [range, setRange] = useState({ presetId: 'mtd', start: toISO(startOfMonth), end: toISO(today) });
  
  const carregarDadosRef = useRef(null);
  const abortControllerRef = useRef(null); // Para cancelar requisições pendentes
  const processingRetryTimeoutRef = useRef(null);
  const loadingRef = useRef(loading);
  const processingRef = useRef(processing);

  useEffect(() => {
    loadingRef.current = loading;
  }, [loading]);

  useEffect(() => {
    processingRef.current = processing;
  }, [processing]);

  const carregarDados = useCallback(async () => {
    // Evitar múltiplas chamadas simultâneas
    if (loadingRef.current && !processingRef.current) {
      return;
    }
    
    // Cancelar requisição anterior se existir
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // Criar novo AbortController para esta requisição
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;
    
    let manterLoadingAtivo = false;
    try {
      setLoading(true);
      const response = await produtosUsadosOSService.buscarProdutosUsadosOS({
        data_inicio: range.start,
        data_fim: range.end,
        assuntos: assuntosSelecionados.length > 0 ? assuntosSelecionados : undefined,
        cidades: cidadesSelecionadas.length > 0 ? cidadesSelecionadas : undefined,
        tecnicos: tecnicosSelecionados.length > 0 ? tecnicosSelecionados : undefined
      }, signal);
      
      // Verificar se foi cancelado
      if (signal.aborted) {
        return;
      }
      
      // O serviço retorna { success: true, data: dadosCompletos }
      // Onde dadosCompletos = { produtos_usados_por_oss: [...], ranking_top10_produtos_por_cidade: [...], ... }
      if (response?.status === 'processing') {
        setProcessing(true);
        manterLoadingAtivo = true;
        if (processingRetryTimeoutRef.current) {
          clearTimeout(processingRetryTimeoutRef.current);
        }
        processingRetryTimeoutRef.current = setTimeout(() => {
          if (carregarDadosRef.current) {
            carregarDadosRef.current();
          }
        }, 3000);

        // Evitar sobrescrever UI com zeros durante processamento.
        return;
      }

      if (response?.data) {
        if (response?.success === true && typeof response.data === 'object' && !Array.isArray(response.data)) {
          setProcessing(false);
          // O serviço já normalizou os dados, então response.data já contém os dados finais
          const dadosFinais = response.data;
          
          // Log de debug quando dados inválidos forem recebidos
          if (dadosFinais?.detalhamento_oss_produtos_utilizados && !Array.isArray(dadosFinais.detalhamento_oss_produtos_utilizados)) {
            console.warn('🔍 Dados recebidos com detalhamento_oss_produtos_utilizados não-array:', {
              tipo: typeof dadosFinais.detalhamento_oss_produtos_utilizados,
              valor: dadosFinais.detalhamento_oss_produtos_utilizados,
              todos_dados: dadosFinais
            });
          }
          
          // Garantir que sempre temos a estrutura completa usando Array.isArray()
          const dadosComEstruturaCompleta = {
            produtos_usados_por_oss: Array.isArray(dadosFinais?.produtos_usados_por_oss) 
              ? dadosFinais.produtos_usados_por_oss 
              : [],
            ranking_top10_produtos_por_cidade: Array.isArray(dadosFinais?.ranking_top10_produtos_por_cidade) 
              ? dadosFinais.ranking_top10_produtos_por_cidade 
              : [],
            detalhamento_oss_produtos_utilizados: Array.isArray(dadosFinais?.detalhamento_oss_produtos_utilizados) 
              ? dadosFinais.detalhamento_oss_produtos_utilizados 
              : [],
            top10_qtd_produtos_por_cidade: Array.isArray(dadosFinais?.top10_qtd_produtos_por_cidade) 
              ? dadosFinais.top10_qtd_produtos_por_cidade 
              : [],
            analise_produtos_movimentados_oss: Array.isArray(dadosFinais?.analise_produtos_movimentados_oss) 
              ? dadosFinais.analise_produtos_movimentados_oss 
              : [],
            evolucao_custo_12m: Array.isArray(dadosFinais?.evolucao_custo_12m)
              ? dadosFinais.evolucao_custo_12m
              : [],
            assuntos_disponiveis: Array.isArray(dadosFinais?.assuntos_disponiveis) 
              ? dadosFinais.assuntos_disponiveis 
              : []
          };
          
          setDadosCompilados(dadosComEstruturaCompleta);
        } else {
          // Se success é false mas tem data, tentar usar mesmo assim
          // Tentar usar response.data se for um objeto válido
          if (response?.data && typeof response.data === 'object' && !Array.isArray(response.data)) {
            const dadosFinais = response.data;
            const dadosComEstruturaCompleta = {
              produtos_usados_por_oss: Array.isArray(dadosFinais?.produtos_usados_por_oss) 
                ? dadosFinais.produtos_usados_por_oss 
                : [],
              ranking_top10_produtos_por_cidade: Array.isArray(dadosFinais?.ranking_top10_produtos_por_cidade) 
                ? dadosFinais.ranking_top10_produtos_por_cidade 
                : [],
              detalhamento_oss_produtos_utilizados: Array.isArray(dadosFinais?.detalhamento_oss_produtos_utilizados) 
                ? dadosFinais.detalhamento_oss_produtos_utilizados 
                : [],
              top10_qtd_produtos_por_cidade: Array.isArray(dadosFinais?.top10_qtd_produtos_por_cidade) 
                ? dadosFinais.top10_qtd_produtos_por_cidade 
                : [],
              analise_produtos_movimentados_oss: Array.isArray(dadosFinais?.analise_produtos_movimentados_oss) 
                ? dadosFinais.analise_produtos_movimentados_oss 
                : [],
              evolucao_custo_12m: Array.isArray(dadosFinais?.evolucao_custo_12m)
                ? dadosFinais.evolucao_custo_12m
                : [],
              assuntos_disponiveis: Array.isArray(dadosFinais?.assuntos_disponiveis) 
                ? dadosFinais.assuntos_disponiveis 
                : []
            };
            setProcessing(false);
            setDadosCompilados(dadosComEstruturaCompleta);
          } else {
            // Se não tem dados válidos, usar estrutura vazia
            setProcessing(false);
            setDadosCompilados({
              produtos_usados_por_oss: [],
              ranking_top10_produtos_por_cidade: [],
              detalhamento_oss_produtos_utilizados: [],
              top10_qtd_produtos_por_cidade: [],
              analise_produtos_movimentados_oss: [],
              evolucao_custo_12m: [],
              assuntos_disponiveis: []
            });
          }
        }
      } else {
        // Se não tem dados, usar estrutura vazia
        setProcessing(false);
        setDadosCompilados({
          produtos_usados_por_oss: [],
          ranking_top10_produtos_por_cidade: [],
          detalhamento_oss_produtos_utilizados: [],
          top10_qtd_produtos_por_cidade: [],
          analise_produtos_movimentados_oss: [],
          evolucao_custo_12m: [],
          assuntos_disponiveis: []
        });
      }
    } catch (error) {
      // Ignorar erros de cancelamento
      if (error.name === 'AbortError' || error.name === 'CanceledError' || error.code === 'ERR_CANCELED') {
        return;
      }
      console.error('Erro ao carregar dados:', error);
      setProcessing(false);
      // Em caso de erro, manter estrutura vazia
      setDadosCompilados({
        produtos_usados_por_oss: [],
        ranking_top10_produtos_por_cidade: [],
        detalhamento_oss_produtos_utilizados: [],
        top10_qtd_produtos_por_cidade: [],
        analise_produtos_movimentados_oss: [],
        evolucao_custo_12m: [],
        assuntos_disponiveis: []
      });
    } finally {
      // Só limpar loading se não foi cancelado
      if (!signal.aborted) {
        if (!manterLoadingAtivo) {
          setLoading(false);
        }
        setUltimaAtualizacao(new Date());
      }
    }
  }, [
    assuntosSelecionados,
    cidadesSelecionadas,
    tecnicosSelecionados,
    range.end,
    range.start
  ]);

  // Carregar dados quando o componente monta OU quando range/assuntos mudarem
  useEffect(() => {
    carregarDados();
  }, [carregarDados]);

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    const selecoesIguais = (a = [], b = []) => (
      a.length === b.length && a.every((item) => b.includes(item))
    );

    const aplicarAssuntosSeMudou = () => {
      if (!selecoesIguais(assuntosSelecionados, assuntosSelecionadosDraft)) {
        setAssuntosSelecionados(assuntosSelecionadosDraft);
      }
    };

    const aplicarCidadesSeMudou = () => {
      if (!selecoesIguais(cidadesSelecionadas, cidadesSelecionadasDraft)) {
        setCidadesSelecionadas(cidadesSelecionadasDraft);
      }
    };

    const aplicarTecnicosSeMudou = () => {
      if (!selecoesIguais(tecnicosSelecionados, tecnicosSelecionadosDraft)) {
        setTecnicosSelecionados(tecnicosSelecionadosDraft);
      }
    };

    const handler = (e) => {
      if (dropdownAssuntosRef.current && !dropdownAssuntosRef.current.contains(e.target)) {
        if (dropdownAssuntosAberto) {
          aplicarAssuntosSeMudou();
        }
        setDropdownAssuntosAberto(false);
      }
      if (dropdownCidadesRef.current && !dropdownCidadesRef.current.contains(e.target)) {
        if (dropdownCidadesAberto) {
          aplicarCidadesSeMudou();
        }
        setDropdownCidadesAberto(false);
      }
      if (dropdownTecnicosRef.current && !dropdownTecnicosRef.current.contains(e.target)) {
        if (dropdownTecnicosAberto) {
          aplicarTecnicosSeMudou();
        }
        setDropdownTecnicosAberto(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [
    assuntosSelecionados,
    assuntosSelecionadosDraft,
    cidadesSelecionadas,
    cidadesSelecionadasDraft,
    tecnicosSelecionados,
    tecnicosSelecionadosDraft,
    dropdownAssuntosAberto,
    dropdownCidadesAberto,
    dropdownTecnicosAberto
  ]);

  // Atualizar ref sempre que carregarDados mudar
  useEffect(() => {
    carregarDadosRef.current = carregarDados;
  }, [carregarDados]);

  // Recarregar dados ao trocar empresa no contexto global
  useEffect(() => {
    const handleCompanyChange = () => {
      if (carregarDadosRef.current) {
        carregarDadosRef.current();
      }
    };

    window.addEventListener('company-changed', handleCompanyChange);
    window.addEventListener('company-context-changed', handleCompanyChange);

    return () => {
      window.removeEventListener('company-changed', handleCompanyChange);
      window.removeEventListener('company-context-changed', handleCompanyChange);
    };
  }, []);

  // Cancelar requisições ao desmontar
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (processingRetryTimeoutRef.current) {
        clearTimeout(processingRetryTimeoutRef.current);
      }
    };
  }, []);


  // API é a fonte de verdade dos filtros globais (assunto/cidade/técnico).
  // Componentes individuais mantêm apenas filtros locais de refinamento.
  const dadosFiltrados = useMemo(() => {
    const dados = dadosCompilados || {
      produtos_usados_por_oss: [],
      ranking_top10_produtos_por_cidade: [],
      detalhamento_oss_produtos_utilizados: [],
      top10_qtd_produtos_por_cidade: [],
      analise_produtos_movimentados_oss: [],
      evolucao_custo_12m: [],
      assuntos_disponiveis: []
    };

    return {
      produtos_usados_por_oss: Array.isArray(dados.produtos_usados_por_oss) ? dados.produtos_usados_por_oss : [],
      ranking_top10_produtos_por_cidade: Array.isArray(dados.ranking_top10_produtos_por_cidade) ? dados.ranking_top10_produtos_por_cidade : [],
      detalhamento_oss_produtos_utilizados: Array.isArray(dados.detalhamento_oss_produtos_utilizados) ? dados.detalhamento_oss_produtos_utilizados : [],
      top10_qtd_produtos_por_cidade: Array.isArray(dados.top10_qtd_produtos_por_cidade) ? dados.top10_qtd_produtos_por_cidade : [],
      analise_produtos_movimentados_oss: Array.isArray(dados.analise_produtos_movimentados_oss) ? dados.analise_produtos_movimentados_oss : [],
      evolucao_custo_12m: Array.isArray(dados.evolucao_custo_12m) ? dados.evolucao_custo_12m : [],
      assuntos_disponiveis: Array.isArray(dados.assuntos_disponiveis) ? dados.assuntos_disponiveis : []
    };
  }, [dadosCompilados]);

  const produtosUsadosPorOSS = useMemo(
    () => dadosFiltrados.produtos_usados_por_oss || [],
    [dadosFiltrados.produtos_usados_por_oss]
  );
  const kpis = useMemo(() => {
    return {
      totalOSS: produtosUsadosPorOSS.length,
      custoTotalOSS: produtosUsadosPorOSS.reduce((total, item) => total + (Number(item.custo) || 0), 0)
    };
  }, [produtosUsadosPorOSS]);

  // Toggle seleção de assunto
  const toggleAssunto = (assunto) => {
    setAssuntosSelecionadosDraft(prev => {
      if (prev.includes(assunto)) {
        return prev.filter(a => a !== assunto);
      } else {
        return [...prev, assunto];
      }
    });
  };

  // Limpar todos os assuntos selecionados
  const limparAssuntos = () => {
    setAssuntosSelecionadosDraft([]);
    setBuscaAssunto('');
  };

  const toggleCidade = (cidade) => {
    setCidadesSelecionadasDraft((prev) => (
      prev.includes(cidade)
        ? prev.filter((c) => c !== cidade)
        : [...prev, cidade]
    ));
  };

  const limparCidades = () => {
    setCidadesSelecionadasDraft([]);
    setBuscaCidade('');
  };

  const toggleTecnico = (tecnico) => {
    setTecnicosSelecionadosDraft((prev) => (
      prev.includes(tecnico)
        ? prev.filter((t) => t !== tecnico)
        : [...prev, tecnico]
    ));
  };

  const limparTecnicos = () => {
    setTecnicosSelecionadosDraft([]);
    setBuscaTecnico('');
  };

  // Filtrar assuntos baseado na busca
  const assuntosFiltrados = useMemo(() => {
    // Garantir que dadosCompilados existe antes de acessar propriedades
    if (!dadosCompilados || !dadosCompilados.assuntos_disponiveis) return [];
    
    if (!buscaAssunto.trim()) {
      return dadosCompilados.assuntos_disponiveis;
    }
    
    const termoBusca = buscaAssunto.toLowerCase().trim();
    return dadosCompilados.assuntos_disponiveis.filter(assunto =>
      assunto.toLowerCase().includes(termoBusca)
    );
  }, [dadosCompilados, buscaAssunto]);

  const cidadesDisponiveis = useMemo(() => {
    const cidades = new Set();
    // Fonte autoritativa para filtros globais: detalhamento (base unificada).
    const fontes = [...(dadosCompilados?.detalhamento_oss_produtos_utilizados || [])];

    fontes.forEach((item) => {
      const cidade = item?.cidade || item?.CIDADE;
      if (cidade) cidades.add(cidade);
    });

    return Array.from(cidades).sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [dadosCompilados]);

  const cidadesFiltradas = useMemo(() => {
    if (!buscaCidade.trim()) return cidadesDisponiveis;
    const termoBusca = buscaCidade.toLowerCase().trim();
    return cidadesDisponiveis.filter((cidade) => cidade.toLowerCase().includes(termoBusca));
  }, [cidadesDisponiveis, buscaCidade]);

  const tecnicosDisponiveis = useMemo(() => {
    const tecnicos = new Set();
    // Fonte autoritativa para filtros globais: detalhamento (base unificada).
    const fontes = [...(dadosCompilados?.detalhamento_oss_produtos_utilizados || [])];

    fontes.forEach((item) => {
      const tecnico = item?.tecnico || item?.TECNICO || item?.['TÉCNICO'];
      if (tecnico) {
        const tecnicoNormalizado = String(tecnico).replace(/\s+/g, ' ').trim();
        if (tecnicoNormalizado) {
          tecnicos.add(tecnicoNormalizado);
        }
      }
    });

    return Array.from(tecnicos).sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [dadosCompilados]);

  const tecnicosFiltrados = useMemo(() => {
    if (!buscaTecnico.trim()) return tecnicosDisponiveis;
    const termoBusca = buscaTecnico.toLowerCase().trim();
    return tecnicosDisponiveis.filter((tecnico) => tecnico.toLowerCase().includes(termoBusca));
  }, [tecnicosDisponiveis, buscaTecnico]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3">
          <h3 className="text-base font-semibold text-[color:var(--text-primary)]">Período</h3>
          <PeriodoDisplay start={range.start} end={range.end} />
          {loading ? (
            <div className="flex items-center text-sm text-[color:var(--accent)]">
              <svg className="h-4 w-4 mr-1 animate-spin" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path></svg>
              <span>Atualizando...</span>
            </div>
          ) : processing ? (
            <div className="flex items-center text-sm text-[color:var(--accent)]">
              <svg className="h-4 w-4 mr-1 animate-spin" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path></svg>
              <span>Processando dados no servidor...</span>
            </div>
          ) : (
            ultimaAtualizacao && (
              <div className="text-xs text-[color:var(--text-muted)]">Atualizado: {ultimaAtualizacao.toLocaleTimeString('pt-BR')}</div>
            )
          )}
        </div>
        <div className="flex flex-col sm:flex-row gap-3">
          {/* Botão Recarregar Dados */}
          <ClearCacheButton
            endpoint="produtos-usados-os"
            params={{
              data_inicio: range.start,
              data_fim: range.end
            }}
            onReload={carregarDados}
          />
          {/* Filtro de Assunto */}
          <div ref={dropdownAssuntosRef} className="relative inline-block">
            <button
              type="button"
              onClick={() => {
                if (dropdownAssuntosAberto) {
                  const selecoesIguais = assuntosSelecionados.length === assuntosSelecionadosDraft.length
                    && assuntosSelecionados.every((item) => assuntosSelecionadosDraft.includes(item));
                  if (!selecoesIguais) {
                    setAssuntosSelecionados(assuntosSelecionadosDraft);
                  }
                  setDropdownAssuntosAberto(false);
                  return;
                }
                setAssuntosSelecionadosDraft(assuntosSelecionados);
                setDropdownAssuntosAberto(true);
              }}
              className={`w-full sm:w-auto inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm font-medium shadow-sm hover:bg-[color:var(--surface-muted)] focus:outline-none focus:ring-2 focus:ring-[color:var(--accent)] focus:ring-offset-2 focus:ring-offset-[color:var(--surface)] ${
                (dropdownAssuntosAberto ? assuntosSelecionadosDraft : assuntosSelecionados).length > 0 
                  ? 'border-[color:var(--accent)] bg-[color:var(--surface)] text-[color:var(--text-primary)]' 
                  : 'border-[color:var(--border)] bg-[color:var(--surface)] text-[color:var(--text-primary)]'
              }`}
            >
              <span className="truncate">
                {(dropdownAssuntosAberto ? assuntosSelecionadosDraft : assuntosSelecionados).length === 0 
                  ? 'Filtrar por Assunto' 
                  : `${(dropdownAssuntosAberto ? assuntosSelecionadosDraft : assuntosSelecionados).length} assunto${(dropdownAssuntosAberto ? assuntosSelecionadosDraft : assuntosSelecionados).length > 1 ? 's' : ''} selecionado${(dropdownAssuntosAberto ? assuntosSelecionadosDraft : assuntosSelecionados).length > 1 ? 's' : ''}`
                }
              </span>
              {(dropdownAssuntosAberto ? assuntosSelecionadosDraft : assuntosSelecionados).length > 0 && (
                <span className="inline-flex items-center rounded-full border border-[color:var(--accent)] bg-[color:var(--surface-muted)] px-2 py-0.5 text-xs font-medium text-[color:var(--accent)]">
                  {(dropdownAssuntosAberto ? assuntosSelecionadosDraft : assuntosSelecionados).length}
                </span>
              )}
              <ChevronDown className="h-4 w-4 text-[color:var(--text-muted)]" />
            </button>

            {dropdownAssuntosAberto && (
              <div className="absolute z-50 mt-2 w-64 rounded-md bg-[color:var(--surface)] shadow-sm border border-[color:var(--border)] focus:outline-none right-0">
                <div className="p-2 border-b border-[color:var(--border)]">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-[color:var(--text-primary)]">Filtrar por Assunto</span>
                    {assuntosSelecionadosDraft.length > 0 && (
                      <button
                        onClick={limparAssuntos}
                        className="text-xs text-[color:var(--accent)] hover:opacity-80"
                      >
                        Limpar
                      </button>
                    )}
                  </div>
                  {/* Campo de busca */}
                  <div className="relative">
                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-[color:var(--text-muted)]" />
                    <input
                      type="text"
                      value={buscaAssunto}
                      onChange={(e) => setBuscaAssunto(e.target.value)}
                      placeholder="Buscar assunto..."
                      className="w-full pl-8 pr-2 py-1.5 text-sm border border-[color:var(--border)] rounded-md bg-[color:var(--surface)] text-[color:var(--text-primary)] placeholder-[color:var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[color:var(--accent)] focus:border-[color:var(--accent)]"
                      onClick={(e) => e.stopPropagation()}
                    />
                    {buscaAssunto && (
                      <button
                        onClick={() => setBuscaAssunto('')}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-[color:var(--text-muted)] hover:text-[color:var(--text-muted)]"
                        type="button"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                </div>
                <div className="max-h-64 overflow-auto p-1">
                  {assuntosFiltrados && assuntosFiltrados.length > 0 ? (
                    assuntosFiltrados.map((assunto) => {
                      const isSelected = assuntosSelecionadosDraft.includes(assunto);
                      return (
                        <label
                          key={assunto}
                          className={`flex items-center px-3 py-2 text-sm hover:bg-[color:var(--surface-muted)] cursor-pointer rounded-md ${
                            isSelected ? 'bg-[color:var(--surface-muted)]' : ''
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleAssunto(assunto)}
                            className="h-4 w-4 text-[color:var(--accent)] focus:ring-[color:var(--accent)] border-[color:var(--border)] rounded"
                          />
                          <span className={`ml-2 flex-1 ${isSelected ? 'font-medium text-[color:var(--text-primary)]' : 'text-[color:var(--text-muted)]'}`}>
                            {assunto}
                          </span>
                        </label>
                      );
                    })
                  ) : (
                    <div className="px-3 py-2 text-sm text-[color:var(--text-muted)]">
                      {buscaAssunto ? 'Nenhum assunto encontrado' : 'Nenhum assunto disponível'}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Filtro de Cidade */}
          <div ref={dropdownCidadesRef} className="relative inline-block">
            <button
              type="button"
              onClick={() => {
                if (dropdownCidadesAberto) {
                  const selecoesIguais = cidadesSelecionadas.length === cidadesSelecionadasDraft.length
                    && cidadesSelecionadas.every((item) => cidadesSelecionadasDraft.includes(item));
                  if (!selecoesIguais) {
                    setCidadesSelecionadas(cidadesSelecionadasDraft);
                  }
                  setDropdownCidadesAberto(false);
                  return;
                }
                setCidadesSelecionadasDraft(cidadesSelecionadas);
                setDropdownCidadesAberto(true);
              }}
              className={`w-full sm:w-auto inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm font-medium shadow-sm hover:bg-[color:var(--surface-muted)] focus:outline-none focus:ring-2 focus:ring-[color:var(--accent)] focus:ring-offset-2 focus:ring-offset-[color:var(--surface)] ${
                (dropdownCidadesAberto ? cidadesSelecionadasDraft : cidadesSelecionadas).length > 0
                  ? 'border-[color:var(--accent)] bg-[color:var(--surface)] text-[color:var(--text-primary)]'
                  : 'border-[color:var(--border)] bg-[color:var(--surface)] text-[color:var(--text-primary)]'
              }`}
            >
              <MapPin className="h-4 w-4" />
              <span className="truncate">
                {(dropdownCidadesAberto ? cidadesSelecionadasDraft : cidadesSelecionadas).length === 0
                  ? 'Filtrar por Cidade'
                  : `${(dropdownCidadesAberto ? cidadesSelecionadasDraft : cidadesSelecionadas).length} cidade${(dropdownCidadesAberto ? cidadesSelecionadasDraft : cidadesSelecionadas).length > 1 ? 's' : ''} selecionada${(dropdownCidadesAberto ? cidadesSelecionadasDraft : cidadesSelecionadas).length > 1 ? 's' : ''}`
                }
              </span>
              {(dropdownCidadesAberto ? cidadesSelecionadasDraft : cidadesSelecionadas).length > 0 && (
                <span className="inline-flex items-center rounded-full border border-[color:var(--accent)] bg-[color:var(--surface-muted)] px-2 py-0.5 text-xs font-medium text-[color:var(--accent)]">
                  {(dropdownCidadesAberto ? cidadesSelecionadasDraft : cidadesSelecionadas).length}
                </span>
              )}
              <ChevronDown className="h-4 w-4 text-[color:var(--text-muted)]" />
            </button>

            {dropdownCidadesAberto && (
              <div className="absolute z-50 mt-2 w-64 rounded-md bg-[color:var(--surface)] shadow-sm border border-[color:var(--border)] focus:outline-none right-0">
                <div className="p-2 border-b border-[color:var(--border)]">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-[color:var(--text-primary)]">Filtrar por Cidade</span>
                    {cidadesSelecionadasDraft.length > 0 && (
                      <button
                        onClick={limparCidades}
                        className="text-xs text-[color:var(--accent)] hover:opacity-80"
                      >
                        Limpar
                      </button>
                    )}
                  </div>
                  <div className="relative">
                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-[color:var(--text-muted)]" />
                    <input
                      type="text"
                      value={buscaCidade}
                      onChange={(e) => setBuscaCidade(e.target.value)}
                      placeholder="Buscar cidade..."
                      className="w-full pl-8 pr-2 py-1.5 text-sm border border-[color:var(--border)] rounded-md bg-[color:var(--surface)] text-[color:var(--text-primary)] placeholder-[color:var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[color:var(--accent)] focus:border-[color:var(--accent)]"
                      onClick={(e) => e.stopPropagation()}
                    />
                    {buscaCidade && (
                      <button
                        onClick={() => setBuscaCidade('')}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-[color:var(--text-muted)] hover:text-[color:var(--text-muted)]"
                        type="button"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                </div>
                <div className="max-h-64 overflow-auto p-1">
                  {cidadesFiltradas.length > 0 ? (
                    cidadesFiltradas.map((cidade) => {
                      const isSelected = cidadesSelecionadasDraft.includes(cidade);
                      return (
                        <label
                          key={cidade}
                          className={`flex items-center px-3 py-2 text-sm hover:bg-[color:var(--surface-muted)] cursor-pointer rounded-md ${
                            isSelected ? 'bg-[color:var(--surface-muted)]' : ''
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleCidade(cidade)}
                            className="h-4 w-4 text-[color:var(--accent)] focus:ring-[color:var(--accent)] border-[color:var(--border)] rounded"
                          />
                          <span className={`ml-2 flex-1 ${isSelected ? 'font-medium text-[color:var(--text-primary)]' : 'text-[color:var(--text-muted)]'}`}>
                            {cidade}
                          </span>
                        </label>
                      );
                    })
                  ) : (
                    <div className="px-3 py-2 text-sm text-[color:var(--text-muted)]">
                      {buscaCidade ? 'Nenhuma cidade encontrada' : 'Nenhuma cidade disponível'}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Filtro de Técnico */}
          <div ref={dropdownTecnicosRef} className="relative inline-block">
            <button
              type="button"
              onClick={() => {
                if (dropdownTecnicosAberto) {
                  const selecoesIguais = tecnicosSelecionados.length === tecnicosSelecionadosDraft.length
                    && tecnicosSelecionados.every((item) => tecnicosSelecionadosDraft.includes(item));
                  if (!selecoesIguais) {
                    setTecnicosSelecionados(tecnicosSelecionadosDraft);
                  }
                  setDropdownTecnicosAberto(false);
                  return;
                }
                setTecnicosSelecionadosDraft(tecnicosSelecionados);
                setDropdownTecnicosAberto(true);
              }}
              className={`w-full sm:w-auto inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm font-medium shadow-sm hover:bg-[color:var(--surface-muted)] focus:outline-none focus:ring-2 focus:ring-[color:var(--accent)] focus:ring-offset-2 focus:ring-offset-[color:var(--surface)] ${
                (dropdownTecnicosAberto ? tecnicosSelecionadosDraft : tecnicosSelecionados).length > 0
                  ? 'border-[color:var(--accent)] bg-[color:var(--surface)] text-[color:var(--text-primary)]'
                  : 'border-[color:var(--border)] bg-[color:var(--surface)] text-[color:var(--text-primary)]'
              }`}
            >
              <User className="h-4 w-4" />
              <span className="truncate">
                {(dropdownTecnicosAberto ? tecnicosSelecionadosDraft : tecnicosSelecionados).length === 0
                  ? 'Filtrar por Técnico'
                  : `${(dropdownTecnicosAberto ? tecnicosSelecionadosDraft : tecnicosSelecionados).length} técnico${(dropdownTecnicosAberto ? tecnicosSelecionadosDraft : tecnicosSelecionados).length > 1 ? 's' : ''} selecionado${(dropdownTecnicosAberto ? tecnicosSelecionadosDraft : tecnicosSelecionados).length > 1 ? 's' : ''}`
                }
              </span>
              {(dropdownTecnicosAberto ? tecnicosSelecionadosDraft : tecnicosSelecionados).length > 0 && (
                <span className="inline-flex items-center rounded-full border border-[color:var(--accent)] bg-[color:var(--surface-muted)] px-2 py-0.5 text-xs font-medium text-[color:var(--accent)]">
                  {(dropdownTecnicosAberto ? tecnicosSelecionadosDraft : tecnicosSelecionados).length}
                </span>
              )}
              <ChevronDown className="h-4 w-4 text-[color:var(--text-muted)]" />
            </button>

            {dropdownTecnicosAberto && (
              <div className="absolute z-50 mt-2 w-64 rounded-md bg-[color:var(--surface)] shadow-sm border border-[color:var(--border)] focus:outline-none right-0">
                <div className="p-2 border-b border-[color:var(--border)]">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-[color:var(--text-primary)]">Filtrar por Técnico</span>
                    {tecnicosSelecionadosDraft.length > 0 && (
                      <button
                        onClick={limparTecnicos}
                        className="text-xs text-[color:var(--accent)] hover:opacity-80"
                      >
                        Limpar
                      </button>
                    )}
                  </div>
                  <div className="relative">
                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-[color:var(--text-muted)]" />
                    <input
                      type="text"
                      value={buscaTecnico}
                      onChange={(e) => setBuscaTecnico(e.target.value)}
                      placeholder="Buscar técnico..."
                      className="w-full pl-8 pr-2 py-1.5 text-sm border border-[color:var(--border)] rounded-md bg-[color:var(--surface)] text-[color:var(--text-primary)] placeholder-[color:var(--text-muted)] focus:outline-none focus:ring-2 focus:ring-[color:var(--accent)] focus:border-[color:var(--accent)]"
                      onClick={(e) => e.stopPropagation()}
                    />
                    {buscaTecnico && (
                      <button
                        onClick={() => setBuscaTecnico('')}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-[color:var(--text-muted)] hover:text-[color:var(--text-muted)]"
                        type="button"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                </div>
                <div className="max-h-64 overflow-auto p-1">
                  {tecnicosFiltrados.length > 0 ? (
                    tecnicosFiltrados.map((tecnico) => {
                      const isSelected = tecnicosSelecionadosDraft.includes(tecnico);
                      return (
                        <label
                          key={tecnico}
                          className={`flex items-center px-3 py-2 text-sm hover:bg-[color:var(--surface-muted)] cursor-pointer rounded-md ${
                            isSelected ? 'bg-[color:var(--surface-muted)]' : ''
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleTecnico(tecnico)}
                            className="h-4 w-4 text-[color:var(--accent)] focus:ring-[color:var(--accent)] border-[color:var(--border)] rounded"
                          />
                          <span className={`ml-2 flex-1 ${isSelected ? 'font-medium text-[color:var(--text-primary)]' : 'text-[color:var(--text-muted)]'}`}>
                            {tecnico}
                          </span>
                        </label>
                      );
                    })
                  ) : (
                    <div className="px-3 py-2 text-sm text-[color:var(--text-muted)]">
                      {buscaTecnico ? 'Nenhum técnico encontrado' : 'Nenhum técnico disponível'}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Filtro de Data */}
          <DateRangeFilterBlur
            value={range}
            loading={loading}
            onChange={(r) => {
              setRange(r);
              // carregarDados será chamado automaticamente quando range mudar via useEffect
            }}
          />
        </div>
      </div>
      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <KPICard
          title="Total de OSS"
          value={kpis.totalOSS.toLocaleString('pt-BR')}
          icon={Package}
          color="blue"
        />
        <KPICard
          title="Custo Total OSS"
          value={`R$ ${kpis.custoTotalOSS.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`}
          icon={Package}
          color="green"
        />
      </div>

      {/* Evolução de Custo Mensal e Custo Médio */}
      <EvolucaoCustoMensalChart
        data={dadosFiltrados.evolucao_custo_12m || []}
        loading={loading}
      />


      {/* Produtos Usados por OSS */}
      <ProdutosUsadosPorOSSTable 
        data={produtosUsadosPorOSS}
        loading={loading}
      />

      {/* Dashboards Top 10 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Top10Dashboard
          title="Top 10 Produtos por Cidade (Quantidade)"
          data={dadosFiltrados.ranking_top10_produtos_por_cidade || []}
          dataKey="produto"
          valueKey="quantidade"
          color="var(--accent)"
        />
        <Top10Dashboard
          title="Top 10 Produtos por Cidade (Quantidade Total)"
          data={dadosFiltrados.top10_qtd_produtos_por_cidade || []}
          dataKey="produto"
          valueKey="quantidade_total"
          color="var(--status-good)"
        />
      </div>

      {/* Detalhamento OSS */}
      <DetalhamentoOSSTable 
        data={dadosFiltrados.detalhamento_oss_produtos_utilizados || []}
        loading={loading}
      />

      {/* Análise de Produtos Movimentados */}
      <AnaliseProdutosMovimentadosTable 
        data={dadosFiltrados.analise_produtos_movimentados_oss || []}
        loading={loading}
      />
    </div>
  );
};

export default ProdutosUsadosOS;
